import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '../contexts/AuthContext'

const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

interface ForgotPasswordModalProps {
  isOpen: boolean
  onClose: () => void
  onBackToLogin: () => void
}

export function ForgotPasswordModal({
  isOpen,
  onClose,
  onBackToLogin,
}: ForgotPasswordModalProps) {
  const { sendPasswordReset } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await sendPasswordReset(data.email)
      if (result.success) {
        setSuccess(true)
      } else {
        setError(result.error || 'Error al enviar el email. Verifica que el email sea correcto.')
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Error al enviar el email. Verifica que el email sea correcto.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    setError(null)
    setSuccess(false)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="bg-casino-black-light border-casino-gold/30 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-2xl text-casino-gold text-center">
            Recuperar Contraseña
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="text-6xl mb-4">📧</div>
            <h3 className="text-xl text-casino-gold mb-2">¡Email Enviado!</h3>
            <p className="text-gray-400 mb-6">
              Te hemos enviado un email con instrucciones para restablecer tu contraseña.
              Revisa tu bandeja de entrada.
            </p>
            <Button variant="gold" onClick={onBackToLogin}>
              Volver a Iniciar Sesión
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <p className="text-gray-400 text-sm text-center">
              Ingresa tu email y te enviaremos instrucciones para restablecer tu contraseña.
            </p>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-red-900/30 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-casino-gold/80">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                {...register('email')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.email && (
                <p className="text-red-400 text-sm">{errors.email.message}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="gold"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Enviando...' : 'ENVIAR INSTRUCCIONES'}
            </Button>

            <button
              type="button"
              onClick={onBackToLogin}
              className="w-full text-sm text-casino-gold/60 hover:text-casino-gold transition-colors"
            >
              ← Volver a iniciar sesión
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
